import type { Membership } from '@space-shell/common/services/membership'
import type { PaginationParams } from '@space-shell/common/services/types'

import { ViewAsUtils } from '@space-shell/common/utils/viewAs'
import { requester } from '@space-shell/config/request'

import { ProductEndpoints } from './constants'
import type {
  DefaultProductResponse,
  ProductUserRoleResponse,
  ProductWithModulesResponse,
  Progress,
  Purchased,
  SalesResponse
} from './types'

const viewAsParams = ViewAsUtils.getParams()

class ProductService {
  static getSaleProducts = async (
    membershipSlug: string,
    productLimit?: PaginationParams,
    onlyHighlight?: boolean
  ) => {
    const { data } = await requester.get<SalesResponse>(ProductEndpoints.sale(membershipSlug), {
      params: {
        ...productLimit,
        onlyHighlight,
        ...viewAsParams
      }
    })

    return { products: data.content, totalElements: data.totalElements }
  }

  static getProgress = async (membershipSlug: string) => {
    const { data } = await requester.get<Progress[]>(ProductEndpoints.progress(membershipSlug), {
      params: viewAsParams
    })

    return data
  }

  static getPurchasedProducts = async (membershipSlug: string, params?: PaginationParams) => {
    const { data } = await requester.get<Purchased>(ProductEndpoints.purchased(membershipSlug), {
      params: {
        ...params,
        ...viewAsParams
      }
    })

    return data
  }

  static getUserRoleByProduct = async (membershipSlug: string, productId: string) => {
    const { data } = await requester.get<ProductUserRoleResponse>(
      ProductEndpoints.getUserRoleByProduct(membershipSlug, productId)
    )

    return data
  }

  static getProductsWithModules = async (membershipSlug: string, params?: PaginationParams) => {
    const { data } = await requester.get<ProductWithModulesResponse>(
      ProductEndpoints.getProductsWithModules(membershipSlug),
      {
        params
      }
    )

    return data
  }

  static getMembershipInfoByProductId = async (productId: string) => {
    const { data } = await requester.get<Pick<Membership, 'slug'>>(
      ProductEndpoints.getMembershipInfoByProductId(productId)
    )

    return data
  }

  static getDefaultProduct = async (membershipId: string) => {
    const { data } = await requester.get<DefaultProductResponse>(
      ProductEndpoints.getDefault(membershipId)
    )

    return data
  }
}

export default ProductService
